import type React from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import Select from 'react-select';
import { FixedSizeList as List } from 'react-window';

import { yupResolver } from '@hookform/resolvers/yup';
import { matchSorter } from 'match-sorter';
import { useForm } from 'react-hook-form';
import { IoIosSearch } from 'react-icons/io';
import { useDispatch, useSelector } from 'react-redux';
import { object, string } from 'yup';
import { addressToLocationRequest } from '../../redux/ducks/MapDucks';
import type { LocationAddressType } from '../../redux/ducks/MapDucks.types';
import type {
  CityType,
  CountryType,
  SelectedPlaceType,
} from '../../redux/ducks/StorageDucks.types';
import type { RootReducerType } from '../../redux/reducers/RootReducer.types';
import Spinner from '../layout/Spinner/Spinner';
import Input from './Input';

import { isEmpty, isNil } from 'lodash-es';
// this one should be lazy loaded
import {
  COUNTRY_WORKER_REQUEST_GET_ALL_COUNTRIES,
  COUNTRY_WORKER_REQUEST_GET_COUNTRY_CITIES,
  type CountryWorkerMessageType,
} from '../../worker/countryWorker.types';

interface PlaceSelectProps {
  value?: SelectedPlaceType;
  onChange: (value: SelectedPlaceType) => void;
  addressRequired?: boolean;
}

const LIST_ITEM_MAX_HEIGHT = 42;
const LIST_ITEM_MAX_SIZE = 4;

const addressToFormattedAddress = (
  address: LocationAddressType,
): string | undefined => {
  let formattedAddress;
  if (address?.house_number) {
    formattedAddress = address?.house_number;
  }

  if (address?.road) {
    formattedAddress = !isEmpty(formattedAddress)
      ? `${formattedAddress}, ${address?.road}`
      : address?.road;
  }

  if (isNil(formattedAddress) && !isEmpty(address?.suburb)) {
    formattedAddress = address?.suburb;
  }

  if (isNil(formattedAddress) && !isEmpty(address?.village)) {
    formattedAddress = address?.village;
  }

  return formattedAddress;
};

const LazyLoadingDropdownList = (props: any) => {
  const { options, children, getValue } = props;
  const [value] = getValue();
  const initialOffset = options.indexOf(value) * LIST_ITEM_MAX_HEIGHT;
  const maxHeight =
    Math.max(1, Math.min(LIST_ITEM_MAX_SIZE, options.length)) *
    LIST_ITEM_MAX_HEIGHT;

  return (
    <div className="z-50">
      <List
        height={maxHeight}
        itemCount={children.length}
        itemSize={LIST_ITEM_MAX_HEIGHT}
        initialScrollOffset={initialOffset}
        width="100%"
      >
        {({ index, style }) => (
          <div
            className="flex min-w-full flex-row items-start justify-start"
            style={style}
          >
            {children[index]}
          </div>
        )}
      </List>
    </div>
  );
};

const PlaceSelect: React.FC<PlaceSelectProps> = ({
  value,
  onChange,
  addressRequired,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const countryWorker: Worker = useMemo(
    () => new Worker(new URL('../../worker/countryWorker.ts', import.meta.url)),
    [],
  );

  const [onWorkerWaiting, setOnWorkerWaiting] = useState<boolean>(false);
  const [onLoadingCountries, setOnLoadingCountries] = useState<boolean>(true);
  const [onLoadingCities, setOnLoadingCities] = useState<boolean>(true);
  const locationToAddressResponse = useSelector(
    (state: RootReducerType) => state.map.locationToAddressResponse,
  );

  const [availableCountries, setAvailableCountries] = useState<CountryType[]>(
    [],
  );

  const [selectedCountry, setSelectedCountry] = useState<CountryType | null>(
    value?.country ?? null,
  );

  const [selectedCity, setSelectedCity] = useState<CityType | null>(
    value?.city ?? null,
  );

  const [availableCities, setAvailableCities] = useState<CityType[]>([]);

  const [searchCountries, setSearchCountries] = useState<CountryType[]>(
    availableCountries || [],
  );

  const [searchCities, setSearchCities] = useState<CityType[]>(availableCities);

  const isOnLocationToAddress = useSelector(
    (state: RootReducerType) => state.map.onLocationToAddressRequest,
  );

  const isOnAddressToLocation = useSelector(
    (state: RootReducerType) => state.map.onAddressToLocationRequest,
  );

  const addressToLocationResponse = useSelector(
    (state: RootReducerType) => state.map.addressToLocationResponse,
  );

  const isSearchingAddress = useMemo(
    () => isOnLocationToAddress || isOnAddressToLocation,
    [isOnLocationToAddress, isOnAddressToLocation],
  );

  const onCountrySelect = (country: CountryType) => {
    setSelectedCountry(country);
    setSelectedCity(null);
    setOnLoadingCities(true);

    if (!country) {
      setAvailableCities([]);
      setSearchCities([]);
    }

    onChange({
      country,
      state: null,
      city: null,
      address: null,
      latlng: null,
    });

    // here the list of city should be generated
    if (countryWorker && country && !onWorkerWaiting) {
      setOnWorkerWaiting(true);
      countryWorker.postMessage({
        request: COUNTRY_WORKER_REQUEST_GET_COUNTRY_CITIES,
        data: country,
      });
    }
  };

  const onCitySelect = (city: CityType) => {
    setSelectedCity(city);

    if (isNil(city)) {
      onChange({
        country: value?.country || null,
        state: value?.state || null,
        city: null,
        address: null,
        latlng: null,
      });
    } else {
      onChange({
        country: city ? city?.country : null,
        state: city ? city?.state : null,
        city,
        address: null,
        latlng: addressRequired ? null : city?.latlng || null,
      });
    }
  };

  const onWorkerMessageReceveid = (
    e: MessageEvent<CountryWorkerMessageType>,
  ) => {
    setOnWorkerWaiting(false);
    const message: CountryWorkerMessageType = e.data;

    if (message.request === COUNTRY_WORKER_REQUEST_GET_ALL_COUNTRIES) {
      setAvailableCountries(message.data);
      setSearchCountries(message.data);
      setOnLoadingCountries(false);
      if (selectedCountry) {
        countryWorker.postMessage({
          request: COUNTRY_WORKER_REQUEST_GET_COUNTRY_CITIES,
          data: selectedCountry,
        });
      }
    } else if (message.request === COUNTRY_WORKER_REQUEST_GET_COUNTRY_CITIES) {
      setAvailableCities(message.data);
      setSearchCities(message.data);
      setOnLoadingCities(false);
    }
  };

  useEffect(() => {
    if (countryWorker && isNil(countryWorker.onmessage)) {
      countryWorker.onmessage = onWorkerMessageReceveid;

      if (!onWorkerWaiting) {
        setOnWorkerWaiting(true);
        countryWorker.postMessage({
          request: COUNTRY_WORKER_REQUEST_GET_ALL_COUNTRIES,
        });
      }
    }
  }, [countryWorker]);

  useEffect(
    () => () => {
      if (countryWorker) {
        countryWorker.terminate();
      }
    },
    [],
  );

  const noAvailableCities = useMemo(() => {
    if (value && !onLoadingCities && availableCities.length <= 0) {
      if (selectedCountry) {
        return true;
      }
    }

    return false;
  }, [onLoadingCities, availableCities]);

  const createSelectCountryLabel = (option: CountryType) => (
    <div className="grid grid-cols-1 items-start justify-start gap-1">
      <div className="flex w-full flex-row items-start justify-start">
        <div className="mr-1 flex flex-col items-center justify-center">
          {option.flag}
        </div>
        <div className="col-span-8 flex grow flex-row items-start justify-start">
          <div className="line-clamp-1 overflow-hidden text-ellipsis break-all">
            {option.label},
            <span className="ml-1 text-neutral-500">{option.region}</span>
          </div>
        </div>
      </div>
    </div>
  );

  const createSelectCityLabel = (option: CityType) => (
    <div className="grid grid-cols-1 items-start justify-start gap-1">
      <div className="flex w-full flex-row items-start justify-start">
        <div className="mr-1 flex flex-col items-center justify-center">
          {option.flag}
        </div>
        <div className="col-span-8 flex grow flex-row items-start justify-start">
          <div className="line-clamp-1 overflow-hidden text-ellipsis break-all">
            {option.label},
            <span className="ml-1 text-neutral-500">
              {option?.state?.label}
            </span>
          </div>
        </div>
      </div>
    </div>
  );

  const countrySelectorRef = useRef<any>(null);
  const citySelectorRef = useRef<any>(null);

  const formSchema = object().shape({
    address: string().required(
      t('static.join.validation_step_location_error_address_required'),
    ),
  });

  const {
    register,
    formState: { errors },
    getValues,
    setValue,
    trigger,
    watch,
  } = useForm({
    resolver: yupResolver(formSchema),
    mode: 'onChange',
  });

  const searchAddress = () => {
    if (!isEmpty(getValues()?.address)) {
      if (value) {
        dispatch(
          addressToLocationRequest({
            countrycodes: value?.country?.value || '',
            lat: selectedCity
              ? selectedCity.latlng[0] || 0.0
              : selectedCountry?.latlng[0] || 0.0,
            lng: selectedCity
              ? selectedCity.latlng[1]
              : selectedCountry?.latlng[1] || 0.0,
            address: getValues()?.address,
            city: value?.city?.value || '',
            state: value?.state?.value || '',
            country: value?.country?.label || '',
          }),
        );
      }
    }
  };

  useEffect(() => {
    if (value && locationToAddressResponse && locationToAddressResponse?.data) {
      const formattedAddress = addressToFormattedAddress(
        locationToAddressResponse.data.address,
      );

      if (!isNil(formattedAddress)) {
        if (
          isNil(value.address?.enteredAddress) ||
          isEmpty(value.address?.enteredAddress)
        ) {
          setValue('address', formattedAddress);
          trigger();
        }

        onChange({
          ...value,
          address: {
            ...value?.address,
            positionAddress: formattedAddress,
            latlng: [
              locationToAddressResponse.data.lat,
              locationToAddressResponse.data.lon,
            ],
          },
          latlng: [
            locationToAddressResponse.data.lat,
            locationToAddressResponse.data.lon,
          ],
        });
      }
    }
  }, [locationToAddressResponse]);

  useEffect(() => {
    if (value && addressToLocationResponse) {
      if (!isNil(addressToLocationResponse.data)) {
        const formattedAddress = addressToFormattedAddress(
          addressToLocationResponse.data.address,
        );

        onChange({
          ...value,
          address: {
            enteredAddress: getValues()?.address,
            positionAddress: formattedAddress,
            zipcode: addressToLocationResponse.data.address.postcode,
            latlng: [
              addressToLocationResponse.data.lat,
              addressToLocationResponse.data.lon,
            ],
          },
          latlng: [
            addressToLocationResponse.data.lat,
            addressToLocationResponse.data.lon,
          ],
        });
      } else if (!isNil(addressToLocationResponse.error)) {
        toast.error(t('static.generic.address_not_found'));

        onChange({
          ...value,
          address: {
            enteredAddress: undefined,
            positionAddress: undefined,
            latlng: undefined,
          },
          latlng: null,
        });
      }
    }
  }, [addressToLocationResponse]);

  useEffect(() => {
    if (!value) {
      setValue('address', '');
    } else if (
      (isNil(value.city) && !noAvailableCities) ||
      isNil(value.country)
    ) {
      setValue('address', '');
    }
  }, [value]);

  useEffect(() => {
    watch((v) => {
      if (value && v && v?.address && isEmpty(v.address)) {
        onChange({
          ...value,
          address: {
            enteredAddress: undefined,
            ...value.address,
          },
        });
      }
    });
  }, [watch]);

  return !onLoadingCountries ? (
    <div className="w-full">
      <span className={`w-full sm:block ${selectedCity && 'hidden'}`}>
        <Select
          ref={countrySelectorRef}
          data-test-id="select-country"
          placeholder={t(
            'static.join.modal_step_location_select_country_placeholder',
          )}
          isClearable
          menuPlacement="auto"
          menuPosition="fixed"
          closeMenuOnSelect
          menuShouldScrollIntoView
          noOptionsMessage={() =>
            t('static.join.modal_step_location_no_countries_label')
          }
          maxMenuHeight={
            Math.max(1, Math.min(LIST_ITEM_MAX_SIZE, searchCountries.length)) *
            LIST_ITEM_MAX_HEIGHT
          }
          options={searchCountries}
          value={selectedCountry}
          onChange={(countryValue: any) => {
            onCountrySelect(countryValue as CountryType);
            countrySelectorRef.current.blur();
          }}
          onInputChange={(inputValue: string) => {
            setSearchCountries(
              matchSorter(availableCountries, inputValue, { keys: ['label'] }),
            );
          }}
          formatOptionLabel={createSelectCountryLabel}
          components={{
            MenuList: LazyLoadingDropdownList,
          }}
          classNames={{
            control: () => 'p-1 border-2',
            input: () => 'text-lg',
            option: () => 'text-lg',
          }}
          theme={(theme: any) => ({
            ...theme,
            borderRadius: 6,
            colors: {
              ...theme.colors,
              primary: '#083344',
              primary25: '#ecfeff',
            },
          })}
        />
      </span>
      {onLoadingCities && selectedCountry && (
        <div className="mb-8 mt-6 flex w-full flex-col items-center justify-center">
          <Spinner color="fill-sky-600" />
        </div>
      )}

      {!onLoadingCities && availableCities.length > 0 && (
        <Select
          ref={citySelectorRef}
          data-test-id="select-city"
          placeholder={t(
            'static.join.modal_step_location_select_city_placeholder',
          )}
          isClearable
          menuShouldScrollIntoView
          closeMenuOnSelect
          menuPlacement="auto"
          menuPosition="fixed"
          className="mt-2"
          noOptionsMessage={() =>
            t('static.join.modal_step_location_no_cities_label')
          }
          components={{
            MenuList: LazyLoadingDropdownList,
          }}
          options={searchCities}
          value={selectedCity}
          maxMenuHeight={
            Math.max(1, Math.min(LIST_ITEM_MAX_SIZE, searchCities.length)) *
            LIST_ITEM_MAX_HEIGHT
          }
          onChange={(cityValue: any) => {
            onCitySelect(cityValue as CityType);
            citySelectorRef.current.blur();
          }}
          onInputChange={(inputValue: any) => {
            setSearchCities(
              matchSorter(availableCities, inputValue, { keys: ['label'] }),
            );
          }}
          formatOptionLabel={createSelectCityLabel}
          classNames={{
            control: () => 'p-1 border-2',
            input: () => 'text-lg',
            option: () => 'text-lg',
          }}
          theme={(theme: any) => ({
            ...theme,
            borderRadius: 6,
            colors: {
              ...theme.colors,
              primary: '#083344',
              primary25: '#ecfeff',
            },
          })}
        />
      )}

      {(selectedCity || noAvailableCities) && addressRequired && (
        <div className="flex min-w-full flex-col items-center justify-center">
          <div className="mt-2 flex min-w-full flex-row items-center justify-between gap-2">
            <span className="grow">
              {isSearchingAddress ? (
                <span className="flex h-[48px] min-w-full flex-col items-center justify-center">
                  <Spinner color="fill-slate-500" />
                </span>
              ) : (
                <Input
                  id="address"
                  label={t(
                    'static.join.modal_step_location_enter_address_label',
                  )}
                  disabled={false}
                  register={register}
                  errors={errors}
                />
              )}
            </span>
            <button
              data-test-id="place-search-btn"
              id="place-search-btn"
              key="place-search-btn"
              aria-label="place-search-btn"
              className="flex h-[50px] min-w-[56px] cursor-pointer flex-col items-center justify-center rounded-xl bg-sky-600 px-1 text-center text-white hover:bg-sky-400"
              type="button"
              onClick={searchAddress}
            >
              <IoIosSearch size={32} />
            </button>
          </div>
        </div>
      )}
    </div>
  ) : (
    <div className="flex size-full flex-col items-center justify-center">
      <Spinner color="fill-sky-600" />
    </div>
  );
};

export default PlaceSelect;
